import React, {Fragment, useEffect, useState} from "react";
import {Popover, Transition} from "@headlessui/react";
import {ChevronDownIcon} from "@heroicons/react/20/solid";
import {t} from "i18next";
import {useAuth} from "../hooks/useAuth";
import Cookies from "universal-cookie";

export const AboutMenu = () => {
    const {isAuthenticated} = useAuth();

    const optionsData = [
        {
            title: t("HEADER.ABOUT_MENU.KULVRUTTANT_SAMIITI.TITLE"),
            description: t("HEADER.ABOUT_MENU.KULVRUTTANT_SAMIITI.DESCRIPTION"),
            icon: "/assets/user-group-icon-outlined.svg",
            hoverIcon: "/assets/user-group-icon-filled.svg",
            url: "/kulvruttant-samiti",
            authenticationRequired: false
        },
        {
            title: t("HEADER.ABOUT_MENU.JOGLEKAR_VAATCHAAL.TITLE"),
            description: t("HEADER.ABOUT_MENU.JOGLEKAR_VAATCHAAL.DESCRIPTION"),
            icon: "/assets/vaatchaal-icon-outlined.svg",
            hoverIcon: "/assets/vaatchaal-icon-filled.svg",
            url: "https://jogkulstorageuat.blob.core.windows.net/siteassets/static-pages-base-data/joglekarkul-vatchal.pdf",
            authenticationRequired: false
        },
        {
            title: t("HEADER.ABOUT_MENU.PRESIDENTS_THOUGHTS.TITLE"),
            description: t("HEADER.ABOUT_MENU.PRESIDENTS_THOUGHTS.DESCRIPTION"),
            icon: "/assets/president-icon-outlined.svg",
            hoverIcon: "/assets/president-icon-filled.svg",
            url: "/manogat",
            authenticationRequired: false
        },
        {
            title: t("HEADER.ABOUT_MENU.ANNUAL_REPORTS.TITLE"),
            description: t("HEADER.ABOUT_MENU.ANNUAL_REPORTS.DESCRIPTION"),
            icon: "/assets/annual-reports-icon-outlined.svg",
            hoverIcon: "/assets/annual-reports-icon-filled.svg",
            url: "/annual-reports",
            authenticationRequired: true
        },
        {
            title: t("HEADER.ABOUT_MENU.KULVRUTTANT_COMMITTEE.TITLE"),
            description: t("HEADER.ABOUT_MENU.KULVRUTTANT_COMMITTEE.DESCRIPTION"),
            icon: "/assets/proceedings-icon-outlined.svg",
            hoverIcon: "/assets/proceedings-icon-filled.svg",
            url: "/proceedings",
            authenticationRequired: true
        },
        {
            title: t("HEADER.ABOUT_MENU.STATUS_REGULATION.TITLE"),
            description: t("HEADER.ABOUT_MENU.STATUS_REGULATION.DESCRIPTION"),
            icon: "/assets/statutes-and-regulations-icon-outlined.svg",
            hoverIcon: "/assets/statutes-and-regulations-icon-filled.svg",
            url: "https://jogkulstorageuat.blob.core.windows.net/siteassets/static-pages-base-data/joglekarkul-ghatana-and-by-laws.pdf",
            authenticationRequired: false
        },
        {
            title: t("HEADER.ABOUT_MENU.INFORMATION.TITLE"),
            description: t("HEADER.ABOUT_MENU.INFORMATION.DESCRIPTION"),
            icon: "/assets/information-icon-outlined.svg",
            hoverIcon: "/assets/information-icon-filled.svg",
            url: "/info",
            authenticationRequired: false
        },
        {
            title: t("HEADER.ABOUT_MENU.CONTACT.TITLE"),
            description: t("HEADER.ABOUT_MENU.CONTACT.DESCRIPTION"),
            icon: "/assets/contact-icon-outlined.svg",
            hoverIcon: "/assets/contact-icon-filled.svg",
            url: "/contact",
            authenticationRequired: false
        },
        {
            title: t("HEADER.ABOUT_MENU.MEMBERSHIP.TITLE"),
            description: t("HEADER.ABOUT_MENU.MEMBERSHIP.DESCRIPTION"),
            icon: "/assets/membership-icon-outlined.svg",
            hoverIcon: "/assets/membership-icon-filled.svg",
            url: '/membership',
            authenticationRequired: false
        },
        {
            title: t("HEADER.ABOUT_MENU.SHATAYUSHI.TITLE"),
            description: t("HEADER.ABOUT_MENU.SHATAYUSHI.DESCRIPTION"),
            icon: "/assets/shatayushi-icon-outlined.svg",
            hoverIcon: "/assets/shatayushi-icon-filled.svg",
            url: '/shatayushi-members',
            authenticationRequired: false
        }
    ];

    const [isTouchDevice, setIsTouchDevice] = useState(false);

    const [open, setOpen] = useState(false);
    const [hover, setHover] = useState(false);
    const [hoveredOptionIndex, setHoveredOptionIndex] = useState(null);


    useEffect(() => {
        const checkTouchDevice = () => setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
        checkTouchDevice();
        window.addEventListener("resize", checkTouchDevice);
        return () => window.removeEventListener("resize", checkTouchDevice);
    }, []);

    return (
        <Popover className="relative flex">
            {({}) => {
                // To prevent the body from scrolling when the popover is open
                document.body.classList.toggle("overflow-y-hidden", open);

                return (
                    <>
                        <button
                            onClick={() => setOpen(!open)}
                            onMouseEnter={() => !isTouchDevice && setHover(true)}
                            onMouseLeave={() => !isTouchDevice && setHover(false)}
                            onBlur={() => setOpen(false)}
                            className={`inline-flex items-center gap-x-1 outline-none`}
                        >
                            <p
                                className="text-[1rem]
                                portrait:sm:text-[0.6rem] portrait:jk:text-[1.15rem]
                                landscape:sm:text-[0.75rem] landscape:jk:text-[1rem]
                                font-custom-medium leading-6 text-gray-900"
                            >
                                {t("HEADER.ABOUT_MENU.TITLE")}
                            </p>
                            <ChevronDownIcon
                                className={`h-[1.25vh] w-[1.25vh]
                                    md:h-[2.25vh] md:w-[2.25vh] 
                                    portrait:sm:h-[0.5rem] portrait:sm:w-[0.5rem]
                                    portrait:jk:h-[1rem] portrait:jk:w-[1rem]
                                    landscape:sm:h-[0.9rem] landscape:sm:w-[0.9rem]
                                    ${hover || open ? "rotate-180" : "rotate-0"}
                                    transition-all duration-150 ease-in-out`}
                                aria-hidden="true"
                            />
                        </button>

                        <Transition
                            show={open || hover}
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                        >
                            <Popover.Panel
                                onMouseEnter={() => setOpen(true)}
                                onMouseLeave={() => setOpen(false)}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute left-1/2 z-20 flex w-screen max-w-max -translate-x-1/2 rounded-2xl
                                portrait:jk:mt-[2rem] landscape:md:mt-[1.5rem] landscape:jk:mt-[1.75rem]"
                            >
                                <div
                                    className={`flex-auto overflow-hidden shadow-lg bg-white 
                                        portrait:jk:w-[17.5rem] portrait:rounded-xl
                                        landscape:md:w-[15rem] landscape:jk:w-[17.5rem] 
                                        landscape:rounded-2xl ring-2 ring-orange-300/5`}
                                >
                                    {/* Menu Options List */}
                                    <div
                                        className="overflow-y-auto flex flex-col
                                        gap-y-[0.5rem] p-[0.25rem] portrait:jk:h-[15rem]
                                        landscape:md:h-[12.5rem] landscape:jk:h-[15rem]"
                                    >
                                        {optionsData.map((option, index) => (
                                            <button
                                                key={index}
                                                onClick={() => {
                                                    if (option.authenticationRequired && !isAuthenticated) {
                                                        new Cookies().set("authRequired", true, {path: '/'});
                                                        window.open('/login', '_self');
                                                    } else {
                                                        window.open(option.url, '_blank');
                                                    }
                                                }}
                                            >
                                                <div
                                                    className={`group relative flex
                                                        ${hoveredOptionIndex === index && 'bg-gray-200'}
                                                        landscape:md:p-[0.4rem] landscape:jk:p-[0.5rem]
                                                        landscape:md:gap-x-[0.55rem] landscape:jk:gap-x-[0.75rem]
                                                        landscape:md:rounded-xl portrait:jk:rounded-xl
                                                        portrait:jk:p-[0.45rem] portrait:jk:gap-x-[0.65rem]
                                                        transition duration-150 ease-[cubic-bezier(0.215,0.61,0.355,1)]`}
                                                    onMouseEnter={() => setHoveredOptionIndex(index)}
                                                    onMouseLeave={() => setHoveredOptionIndex(null)}
                                                    onTouchStart={() => setHoveredOptionIndex(index)}
                                                    onTouchEnd={() => setHoveredOptionIndex(null)}
                                                >
                                                    <div
                                                        className={`flex items-center justify-center aspect-square
                                                        landscape:md:h-[1.85rem] landscape:jk:h-[2.25rem]
                                                        landscape:md:p-[0.45rem] landscape:jk:p-[0.6rem]
                                                        landscape:md:rounded-lg portrait:jk:rounded-lg
                                                        portrait:jk:h-[2.25rem] portrait:jk:p-[0.6rem]
                                                        ${hoveredOptionIndex === index ? 'bg-orange-400' : 'bg-gray-50'}
                                                        transition duration-150 ease-[cubic-bezier(0.215,0.61,0.355,1)]`}
                                                    >
                                                        <img
                                                            alt={option.title}
                                                            src={hoveredOptionIndex === index ? option.hoverIcon : option.icon}
                                                            className="h-full w-auto aspect-square"
                                                        />
                                                    </div>
                                                    <div
                                                        className={"flex flex-col"}
                                                    >
                                                        <h1
                                                            className="font-custom-semi-bold leading-tight text-gray-900
                                                                landscape:md:text-[0.65rem] landscape:jk:text-[0.8rem]
                                                                portrait:jk:text-[0.8rem] text-left"
                                                        >
                                                            {option.title}
                                                        </h1>
                                                        <p
                                                            className="mt-0.5
                                                                font-custom-regular leading-tight text-gray-700
                                                                landscape:md:text-[0.5rem] landscape:jk:text-[0.65rem]
                                                                portrait:jk:text-[0.65rem] text-left"
                                                        >
                                                            {option.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </Popover.Panel>
                        </Transition>
                    </>
                );
            }}
        </Popover>
    );
};

